import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import pricingApi from '../services/pricingApi';

// Shared key so a policy save refetches the guidance that displays the policy.
export const PRICING_GUIDANCE_KEY = (propertyId) => ['pricing', 'guidance', propertyId];

/**
 * Guidance for ONE unit. Disabled until a unit is chosen — the page is
 * deliberately single-unit, so there is no portfolio-wide request to make.
 */
export const usePricingGuidance = (propertyId) =>
  useQuery({
    queryKey: PRICING_GUIDANCE_KEY(propertyId),
    queryFn: () => pricingApi.getGuidance(propertyId),
    enabled: Boolean(propertyId),
    // The page's whole loop is: read a price here → click through to the calendar →
    // change it → come back. The app-wide 5-minute staleTime served the pre-change
    // price on return, on the one screen that sent ops to change it. Operational data
    // gets a short window here, as useArrivalReadiness does for the same reason.
    staleTime: 0
  });

/**
 * Quoting is a mutation, not a query: ops asks for specific dates and expects a
 * fresh answer each time. Caching a quote would let a stale total survive a price
 * change made in the calendar thirty seconds earlier.
 */
export const usePricingQuote = (propertyId) =>
  useMutation({
    mutationFn: (range) => pricingApi.getQuote(propertyId, range)
  });

export const PRICING_AIRBNB_OFFERS_KEY = (propertyId) => ['pricing', 'airbnbOffers', propertyId];

// Retry once, and ONLY when no answer came back — a refusal (403/404, or the 503 that means the card
// is switched off) is final. The app-wide retry would hold a disabled card on a spinner for seconds.
const retryOnlyWithoutAnswer = (failureCount, error) => !error?.response && failureCount < 1;

/**
 * The unit's Airbnb discounts & promotions card. Its own query, apart from the guidance: a Channex
 * problem must never take the rest of the page down with it.
 */
export const useAirbnbOffers = (propertyId) =>
  useQuery({
    queryKey: PRICING_AIRBNB_OFFERS_KEY(propertyId),
    queryFn: () => pricingApi.getAirbnbOffers(propertyId),
    enabled: Boolean(propertyId),
    retry: retryOnlyWithoutAnswer,
    // The copy changes at most nightly or on «تحديث» (which refetches below); a minute is plenty.
    staleTime: 60 * 1000
  });

/**
 * «تحديث». Never retried — each try is a Channex read, and the backend refuses a second within a
 * minute anyway. onSettled, not onSuccess, and the promise is RETURNED: the button stays busy until
 * the fresh card is in, whatever the read's outcome.
 */
export const useRefreshAirbnbOffers = (propertyId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => pricingApi.refreshAirbnbOffers(propertyId),
    retry: false,
    onSettled: () => queryClient.invalidateQueries({ queryKey: PRICING_AIRBNB_OFFERS_KEY(propertyId) })
  });
};

/**
 * The reference/floor/tiers write. Invalidates the guidance query because the quote
 * box's warnings and the settings strip both read the policy back from it.
 */
export const useSavePricingPolicy = (propertyId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (policy) => pricingApi.savePolicy(propertyId, policy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRICING_GUIDANCE_KEY(propertyId) });
    }
  });
};
